'use client';

import { cva, VariantProps } from 'class-variance-authority';
import { ComponentPropsWithRef, MouseEvent, useRef } from 'react';
import { cn } from '@/shared/lib/cn';
import { WithDataAttributes } from '@/shared/model/WithDataAttributes';
import { AnimatedNumber } from '@/shared/ui/animated-number';
import { mergeRefs } from '@/shared/ui/form/utils/mergeRefs';
import { Icon } from '@/shared/ui/icon';
import './amount-input.scss';

const changeInputValueReactively = (el: HTMLInputElement, value: string) => {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor?.(
        window.HTMLInputElement.prototype,
        'value',
    )?.set;
    nativeInputValueSetter?.call(el, value);
    const event = new Event('change', { bubbles: true });
    el.dispatchEvent(event);
};

const amountInputVariant = cva('amount-input', {
    variants: {
        size: {
            sm: 'amount-input--sm',
            md: 'amount-input--md',
        },
        variant: {
            default: null,
            hiding: 'amount-input--hidden',
        },
    },
    defaultVariants: {
        size: 'md',
        variant: 'default',
    },
});

type Props = VariantProps<typeof amountInputVariant> &
    Omit<ComponentPropsWithRef<'input'>, 'size'> & {
        wrapperProps?: WithDataAttributes<Omit<ComponentPropsWithRef<'div'>, 'className'>>;
        minusButtonProps?: WithDataAttributes<ComponentPropsWithRef<'button'>>;
        plusButtonProps?: WithDataAttributes<ComponentPropsWithRef<'button'>>;
        disableMinus?: boolean;
        disablePlus?: boolean;
    };

export const AmountInput = ({
    value = 1,
    step = 1,
    min,
    max,
    variant = 'default',
    size = 'md',
    className,
    ref,
    wrapperProps,
    minusButtonProps,
    plusButtonProps,
    disableMinus = false,
    disablePlus = false,
    ...props
}: Props) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleStepChange = (type: 'increment' | 'decrement') => {
        const current = inputRef.current?.value ? parseFloat(inputRef.current.value) : 0;
        const increment = typeof step === 'string' ? parseFloat(step) : step;
        const newValue = type === 'increment' ? current + increment : current - increment;
        if ((typeof max === 'number' && newValue > max) || (typeof min === 'number' && newValue < min)) {
            return;
        }
        changeInputValueReactively(inputRef.current!, newValue.toString());
    };

    const parsedValue = parseFloat(`${value}`);
    const displayValue = isNaN(parsedValue) ? 0 : parsedValue;
    const isHidden = variant === 'hiding' && displayValue <= 0;

    const handleMinusClick = (event: MouseEvent<HTMLButtonElement>) => {
        handleStepChange('decrement');
        minusButtonProps?.onClick?.(event);
    };

    const handlePlusClick = (event: MouseEvent<HTMLButtonElement>) => {
        handleStepChange('increment');
        plusButtonProps?.onClick?.(event);
    };

    return (
        <div
            className={amountInputVariant({ size, variant: isHidden ? 'hiding' : 'default', className })}
            {...wrapperProps}
        >
            <button
                type="button"
                className={cn('amount-input__button', 'amount-input__button--minus')}
                disabled={disableMinus}
                {...minusButtonProps}
                onClick={handleMinusClick}
            >
                <Icon name="minus" width={24} height={24} />
            </button>
            <input
                ref={mergeRefs([ref, inputRef])}
                type="number"
                className={cn('amount-input__input')}
                value={value}
                disabled
                tabIndex={-1}
                {...props}
            />

            <span
                className={cn('amount-input__value', isHidden && 'amount-input__value--hidden')}
                aria-hidden
            >
                <AnimatedNumber value={displayValue} />
            </span>
            <button
                type="button"
                className={cn('amount-input__button', 'amount-input__button--plus')}
                disabled={disablePlus}
                {...plusButtonProps}
                onClick={handlePlusClick}
            >
                <Icon name="plus" width={24} height={24} />
            </button>
        </div>
    );
};
